import { useCallback, useEffect, useState } from 'react'
import { Alert, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { Button, Icon } from '@/components/ui'
import { DocumentPreviewDrawer } from '@/components/Database/DocumentPreviewDrawer'
import { useDatabase } from '@/hooks/useDatabase'
import { useAuth } from '@/hooks/useAuth'
import { useT } from '@/i18n/useT'
import { formatAmount, formatDate, formatDateTime } from '@/utils/inboxFormat'
import { TRASH_RETENTION_DAYS } from '@/types/database'
import type { TrashedInvoice } from '@/types/database'

interface TrashDialogProps {
  open: boolean
  onClose: () => void
  /** Called after a restore or a permanent delete so the page can reload its grid. */
  onChanged: () => void
}

/** Invoice dates may be legacy DD.MM.YYYY strings; `new Date()` would swap day and month. */
function formatInvoiceDate(value: string | null): string {
  if (!value) return '–'
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : value
}

const cellStyle = { padding: '8px 10px', textAlign: 'left' as const, whiteSpace: 'nowrap' as const }

/**
 * Mounts its body only while open: no fetch, no auth lookup and no hooks for a
 * closed dialog, so the Database page stays inert until the user opens it.
 */
export function TrashDialog(props: TrashDialogProps) {
  return props.open ? <TrashDialogBody {...props} /> : null
}

function TrashDialogBody({ open, onClose, onChanged }: TrashDialogProps) {
  const { t } = useT()
  const { getTrash, restoreInvoice, purgeInvoice } = useDatabase()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  const [items, setItems] = useState<TrashedInvoice[]>([])
  const [retentionDays, setRetentionDays] = useState(TRASH_RETENTION_DAYS)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [preview, setPreview] = useState<TrashedInvoice | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getTrash()
      setItems(res.data)
      setRetentionDays(res.retentionDays)
    } catch (err) {
      console.error('[trash] load failed', err)
      setError(t('Could not load the trash'))
    } finally {
      setLoading(false)
    }
  }, [getTrash, t])

  // The body only exists while the dialog is open, so this is the fetch-on-open
  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRestore = async (id: string) => {
    if (busyId) return
    setBusyId(id)
    setError(null)
    try {
      const res = await restoreInvoice(id)
      setItems((prev) => prev.filter((i) => i.id !== id))
      setNotice(
        res.duplicateIds.length > 0
          ? t('Restored — an invoice with the same supplier and number already exists.')
          : t('Restored')
      )
      onChanged()
    } catch (err) {
      console.error('[trash] restore failed', err)
      setError(t('Could not restore the invoice'))
    } finally {
      setBusyId(null)
    }
  }

  const handlePurge = async (id: string) => {
    if (busyId) return
    setBusyId(id)
    setError(null)
    try {
      await purgeInvoice(id)
      setItems((prev) => prev.filter((i) => i.id !== id))
      setConfirmId(null)
      onChanged()
    } catch (err) {
      console.error('[trash] purge failed', err)
      setError(t('Could not delete the invoice permanently'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <Dialog open={open && !preview} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>{t('Trash')}</DialogTitle>
        <DialogContent>
          <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 12 }}>
            {t('Deleted invoices are kept for {n} days.').replace('{n}', String(retentionDays))}
          </div>
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {notice && (
            <Alert
              severity={notice === t('Restored') ? 'success' : 'warning'}
              sx={{ mb: 1.5 }}
              onClose={() => setNotice(null)}
            >
              {notice}
            </Alert>
          )}
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <CircularProgress />
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
              {t('The trash is empty.')}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--line)' }}>
                    <th style={cellStyle}>{t('Supplier')}</th>
                    <th style={cellStyle}>{t('Invoice number')}</th>
                    <th style={cellStyle}>{t('Invoice date')}</th>
                    <th style={{ ...cellStyle, textAlign: 'right' }}>{t('Gross amount')}</th>
                    <th style={cellStyle}>{t('Deleted on')}</th>
                    <th style={cellStyle}>{t('Deleted by')}</th>
                    <th style={cellStyle}>{t('Deleted for good on')}</th>
                    <th style={cellStyle} />
                  </tr>
                </thead>
                <tbody>
                  {items.map((inv) => (
                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--line)' }}>
                      <td style={cellStyle}>{inv.supplierName}</td>
                      <td style={cellStyle}>{inv.rechnungsnummer}</td>
                      <td style={cellStyle}>{formatInvoiceDate(inv.dateInvoice)}</td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{formatAmount(inv.brutto, 'EUR')}</td>
                      <td style={cellStyle}>{formatDateTime(inv.deletedAt)}</td>
                      <td style={cellStyle}>{inv.deletedByName ?? '–'}</td>
                      <td style={cellStyle}>{formatDate(inv.purgeAt)}</td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          {inv.sourceDocumentId && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title={t('View invoice')}
                              onClick={() => setPreview(inv)}
                            >
                              <Icon name="eye" size={14} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId !== null}
                            onClick={() => void handleRestore(inv.id)}
                          >
                            <Icon name="refresh" size={14} strokeWidth={2.2} />
                            {t('Restore')}
                          </Button>
                          {isAdmin &&
                            (confirmId === inv.id ? (
                              <>
                                <span style={{ fontSize: 12 }}>
                                  {t('Delete this invoice permanently? This cannot be undone.')}
                                </span>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  disabled={busyId !== null}
                                  onClick={() => void handlePurge(inv.id)}
                                >
                                  {t('Delete permanently')}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>
                                  {t('Cancel')}
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busyId !== null}
                                onClick={() => setConfirmId(inv.id)}
                              >
                                <Icon name="trash" size={14} />
                                {t('Delete permanently')}
                              </Button>
                            ))}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>
      <DocumentPreviewDrawer invoice={preview} onClose={() => setPreview(null)} />
    </>
  )
}
